/**
 * `Tabs` (W1-12 ui-kit-i18n; contract TabsProps): Radix tabs. Arrow keys move between tabs (automatic
 * activation, wrapping), Home/End jump to the ends; the active tab's panel (`children`) is labelled by its tab.
 * Tabs show an optional icon and badge (e.g. a problem count) next to the label.
 */

import * as RT from '@radix-ui/react-tabs';
import { Icon } from './Icon';
import { cx } from './internal/cx';
import s from './Tabs.module.css';
import type { TabsProps } from './types';

export interface TabsExtraProps {
  /** Accessible name of the tab list. */
  readonly label?: string;
  readonly className?: string;
  /** Visual density: 'line' (underline, dock tabs) or 'pill' (segmented look). */
  readonly variant?: 'line' | 'pill';
}

export function Tabs<T extends string>(props: TabsProps<T> & TabsExtraProps) {
  const tabs = props.tabs;
  return (
    <RT.Root
      className={cx(s.root, props.className)}
      value={props.value}
      onValueChange={(v) => {
        const tab = tabs.find((x) => x.value === v);
        if (tab !== undefined) props.onChange(tab.value);
      }}
    >
      <RT.List className={cx(s.list, props.variant === 'pill' && s.pill)} aria-label={props.label}>
        {tabs.map((tab) => (
          <RT.Trigger key={tab.value} value={tab.value} className={s.tab}>
            {tab.icon !== undefined && <Icon name={tab.icon} size={14} />}
            <span className={s.label}>{tab.label}</span>
            {tab.badge !== undefined && tab.badge !== null && <span className={s.badge}>{tab.badge}</span>}
          </RT.Trigger>
        ))}
      </RT.List>
      {props.children !== undefined && props.children !== null && (
        <RT.Content value={props.value} className={s.panel}>
          {props.children}
        </RT.Content>
      )}
    </RT.Root>
  );
}
